// Utils
import LanguagesUtil from './LanguagesUtil';

const UNITS = {
    squareMeters: {
        'm²': { min: 100, multiplier: 10000 },
        ha: { min: 100000000, multiplier: 100000000 },
        'km²': { min: 10000000000, multiplier: 10000000000 }
    },
    meters: {
        cm: { min: 0, multiplier: 1 },
        m: { min: 1000, multiplier: 100 },
        hm: { min: 100000, multiplier: 10000 },
        km: { min: 1000000, multiplier: 100000 }
    },
    kilograms: {
        g: { min: 0, multiplier: 1 },
        kg: { min: 10, multiplier: 1000 },
        t: { min: 10000, multiplier: 1000000 }
    },
    kilowattHours: {
        kWh: { min: 0, multiplier: 1 },
        MWh: { min: 10, multiplier: 1000 },
        GWh: { min: 10000, multiplier: 1000000 }
    },
    currencies: {
        '€': { min: 0, multiplier: 1 },
        'k€': { min: 10, multiplier: 1000 },
        'M€': { min: 10000, multiplier: 1000000 }
    }
};

export default class FormattersUtil {
    static formatUnits(targetedUnit, value, defaultUnit, showUnit) {
        if (value.toString().length === 3 && !(value % 1)) return showUnit ? `${value} ${defaultUnit}` : value;
        const isNegative = value < 0;
        if (isNegative) value *= -1;
        value = targetedUnit[defaultUnit] ? value * targetedUnit[defaultUnit].multiplier : value;

        let formattedValue;
        for (const [unit, config] of Object.entries(targetedUnit))
            if (value >= config.min) {
                formattedValue = Math.floor(((value / config.multiplier) + Number.EPSILON) * 100) / 100;

                // Permet de rester à l'unité courante si jamais elle permet d'avoir 3 chiffres significatifs (ex: 100 ou 10.5)
                const stringValue = formattedValue.toString();
                let isValueOk = false;
                if (formattedValue.toFixed(0).length === 3) {
                    isValueOk = true;
                    formattedValue = Math.floor(formattedValue);
                } else if (stringValue.includes('.')) {
                    const number = stringValue.split('.')[0];
                    const decimals = stringValue.split('.')[1];
                    if (number.length <= 2) {
                        if (number.length === 2 && decimals[0] !== '0') formattedValue = Math.floor(formattedValue * 10) / 10; // 10.96 => 10.9
                        else if (number.length === 2 && decimals[0] === '0') formattedValue = Math.round(formattedValue * 10) / 10; // 10.05 => 10.1
                        isValueOk = true;
                    } else if (number.length === 3) {
                        formattedValue = Math.floor(formattedValue); // 100.5 => 100
                        isValueOk = true;
                    }
                }
                formattedValue = new Intl.NumberFormat(LanguagesUtil.getLocale()).format(formattedValue);

                if (showUnit) {
                    formattedValue += ` ${unit}`;
                    if (isValueOk) break;
                }
            }
            else break;

        if (isNegative) value *= -1;
        if (!formattedValue) formattedValue = showUnit ? `${value} ${defaultUnit}` : value;
        return formattedValue;
    }

    static formatSquareMeter(value, { defaultUnit = 'm²', showUnit = true } = {}) {
        if ((!value && value !== 0) || isNaN(value)) return;
        return this.formatUnits(UNITS.squareMeters, value, defaultUnit, showUnit);
    }

    static formatMeter(value, { defaultUnit = 'cm', showUnit = true } = {}) {
        if ((!value && value !== 0) || isNaN(value)) return;
        return this.formatUnits(UNITS.meters, value, defaultUnit, showUnit);
    }

    static formatKilogram(value, { defaultUnit = 'g', showUnit = true } = {}) {
        if ((!value && value !== 0) || isNaN(value)) return;
        return this.formatUnits(UNITS.kilograms, value, defaultUnit, showUnit);
    }

    static formatKilowattHour(value, { defaultUnit = 'kWh', showUnit = true } = {}) {
        if ((!value && value !== 0) || isNaN(value)) return;
        return this.formatUnits(UNITS.kilowattHours, value, defaultUnit, showUnit);
    }

    static formatEuro(value, { defaultUnit = '€', showUnit = true } = {}) {
        if ((!value && value !== 0) || isNaN(value)) return;
        return this.formatUnits(UNITS.currencies, value, defaultUnit, showUnit);
    }

    static formatNumber(value) {
        const valueAsString = value.toString();
        const index = valueAsString.indexOf('.') !== -1 ? valueAsString.indexOf('.') : valueAsString.indexOf(',');
        if (index !== -1 && valueAsString.length > 4) {
            const nbDecimals = valueAsString.length - (index + 1);
            const nbNumbersBeforeDecimal = valueAsString.length - (nbDecimals + 1);
            if (nbNumbersBeforeDecimal >= 3) value = Math.round(value);
            else {
                const nbDecimalsToKeep = 3 - nbNumbersBeforeDecimal;
                let stringLengthToKeep = nbNumbersBeforeDecimal + 1 + nbDecimalsToKeep;
                if (stringLengthToKeep > valueAsString.length) stringLengthToKeep = valueAsString.length;
                value = parseFloat(valueAsString.substring(0, stringLengthToKeep));
            }
        }
        return value;
    }

    //? Séparateurs de milliers et de décimales de la locale courante (ex: fr-FR => { group: ' ', decimal: ',' })
    static getNumberSeparators() {
        // 5 chiffres : es-ES ne groupe pas les nombres à 4 chiffres
        const parts = new Intl.NumberFormat(LanguagesUtil.getLocale()).formatToParts(12345.6);
        return {
            group: parts.find(part => part.type === 'group')?.value || ' ',
            decimal: parts.find(part => part.type === 'decimal')?.value || '.'
        };
    }

    //? Reconvertit un nombre formaté selon la locale (ex: '1,081.91' ou '1 081,91') en nombre exploitable
    static parseLocaleNumber(value) {
        if (typeof value === 'number') return value;
        if (!value) return 0;
        const { group, decimal } = this.getNumberSeparators();
        const parsedValue = parseFloat(
            value.toString().split(group).join('').replace(decimal, '.').replace(/[^\d.-]/g, '')
        );
        return isNaN(parsedValue) ? 0 : parsedValue;
    }

    static getUnit(string) {
        string = string.toString();
        if (!/\d/.test(string)) return;

        let value = '';
        for (let i = 0; i < string.length; i++)
            if (!isNaN(string[i]) || [',', '.'].includes(string[i]))
                value += string[i];
        return value.length ? string.replace(value, '') : null;
    }

    static checkName(input) {
        if (!input || input.trim().length === 0) return false;
        let res = input.trim().replace(" ", "").match(/^[a-zA-ZáàâäãåçéèêëíìîïñóòôöõúùûüýÿæœÁÀÂÄÃÅÇÉÈÊËÍÌÎÏÑÓÒÔÖÕÚÙÛÜÝŸÆŒ\s-']{1,60}$/);
        return res ? true : false;
    }

    static numberPrettyBytesSI(bytes = 0) {
        const units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        let l = 0, n = parseInt(bytes, 10) || 0;
        while (n >= 1024 && ++l) n = n / 1024;
        return (n.toFixed(n < 10 && l > 0 ? 1 : 0) + ' ' + units[l]);
    }

    static getBaseURL(requestURL) {
        let url = requestURL.slice();
        if (url.indexOf("?") > -1) {
            url = url.substr(0, url.indexOf("?"));
            if (url[url.length - 1] !== '/') url += "/";
        }
        return url.substr(0, url.lastIndexOf("/") + 1);
    }

    static checkString(input, minLength, maxLength) {
        if (!input?.trim()) return false;
        if (minLength && input.length < minLength) return false;
        if (maxLength && input.length > maxLength) return false;
        return true;
    }

    static isURL(userInput) {
        let res = userInput.match(/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_.~#?&//=]*)/g);
        return res ? true : false;
    }

    static formatLastNameAndFirstName(lastname, firstname) {
        if (!lastname) return firstname;
        else return firstname + " " + lastname[0] + ".";
    }

    static checkIfNbElementsReached(nbElements, userSubscriptionNbElements) {
        return !(userSubscriptionNbElements === -1 || nbElements < userSubscriptionNbElements);
    }

    static checkEmail(email) {
        if (!email || email.trim().length === 0) return false;
        const regExp = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
        return regExp.test(email.trim());
    }

    static getNormalizedString(string, { spaceBetweenWords = false } = {}) {
        if (!string?.normalize) return string || string === 0 ? `${string}` : string;
        // return string.normalize('NFD').replace(/\s+/g, '').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9_-]+/g, '').toLowerCase();
        return string.normalize('NFD').replace(/\s+/g, spaceBetweenWords ? ' ' : '').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    static searchList(options, searchQuery) {
        const search = FormattersUtil.getNormalizedString(`${searchQuery}`);
        return options.filter(option => FormattersUtil.getNormalizedString(option.rawText || option.text)?.includes(search) || option.value === 'select-all');
    }

    static countDecimals(value) {
        if (Math.floor(value) === value) return 0;
        return value.toString().split(".")[1].length || 0;
    }

    static sortByAlphabeticalOrder(list) {
        return list.sort((a, b) => {
            var textA = (a.rawText || a.text).toUpperCase();
            var textB = (b.rawText || b.text).toUpperCase();
            return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
        });
    }

    static lowerFirstLetter(string) {
        return string.charAt(0).toLowerCase() + string.slice(1);
    }

    static sortRoles(roles = []) {
        if (!Array.isArray(roles)) return [];
        return roles.toSorted((a, b) => {
            const ordreDesTypes = {
                'owner': 1,
                'manager': 2,
                'collaborator': 3,
                'null': 4,
                'default': 5
            };

            const type1 = a.type || 'null';
            const type2 = b.type || 'null';

            const comparaisonParType = ordreDesTypes[type1] - ordreDesTypes[type2];

            if (comparaisonParType === 0 || isNaN(comparaisonParType)) return a.label.localeCompare(b.label);
            return comparaisonParType;
        });
    }

    static isValidUrl(url) {
        if (!url?.length) return false;
        const urlPattern = /(?:https?):\/\/(\w+:?\w*)?(\S+)(:\d+)?(\/|\/([\w#!:.?+=&%!\\/-]))?/;
        return urlPattern.test(url);
    }

    static getTrunkCircumference(circumference, targetUnit, originalUnit = 'circumference') {
        if (!circumference || originalUnit === targetUnit) return circumference;

        switch (targetUnit) {
            case 'diameter': return Math.round((circumference / Math.PI) * 100) / 100;
            case 'circumference': return Math.round((circumference * Math.PI) * 100) / 100;
            default: return circumference;
        }
    }

    static formatFormulaCustomField(customField, value) {
        value = Number(value);

        const format = customField.formulaCustomFieldFormats?.find(format => {
            if (format.key.includes('-')) {
                const values = format.key.split('-');
                return value >= Number(values[0]) && value <= Number(values[1]);
            }
            else if (format.key.includes('<')) return value < Number(format.key.slice(1));
            else if (format.key.includes('>')) return value > Number(format.key.slice(1));
            else return value === Number(format.key);
        });

        return format?.value.trim() ? format.value : (value + (customField.unit?.trim() ? customField.unit : ''));
    }

    //? Pour un texte saisi qui doit passer par une chaîne HTML (template d'une bibliothèque) ; sinon, préférer textContent
    static escapeHtml(text) {
        const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
        return `${text ?? ''}`.replace(/[&<>"']/g, char => entities[char]);
    }

    static areStringsEqual(str1, str2) {
        if (!str1 && !str2) return true;
        if (!str1 || !str2) return false;
        return this.getNormalizedString(str1) === this.getNormalizedString(str2);
    }

    static parseNumberLocale(stringNumber) {
        // Format des nombres de la langue de l'application
        const locale = LanguagesUtil.getLocale();
        const example = Intl.NumberFormat(locale).format(1.1);
        const decimalSeparator = example[1]; // "." ou ","

        // On supprime l'unité, les espaces, et les apostrophes suisses
        let cleaned = stringNumber
            .replace(/[^\d.,'-]/g, '')   // garde chiffres + ., - , '
            .replace(/’/g, '')           // apostrophe typographique (Suisse)
            .replace(/'/g, '')           // apostrophe simple (Suisse)
            .replace(/\s/g, '');         // espaces ou espaces fines

        // Si séparateur décimal = '.', on enlève les virgules (milliers)
        if (decimalSeparator === '.') cleaned = cleaned.replace(/,/g, '');
        // Si séparateur décimal = ',', on enlève les points et convertir la virgule en point
        else {
            cleaned = cleaned.replace(/\./g, '');
            cleaned = cleaned.replace(',', '.');
        }

        return Number(cleaned);
    }
}
